import React from 'react';
import { Container, Typography, Button, Box, Grid, Card, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
// Import icons
import harmonyIcon from '../assets/HARMONY-ICON.svg';
import prosperityIcon from '../assets/PROSPERITY-ICON.svg';
import loveIcon from '../assets/LOVE-ICON.svg';
import background from '../assets/BACK-FLOWERS.png';
// Import background image directly
import fondoCajas from '../assets/FONDO-CAJAS.png';
import WhatsAppButton from '../components/WhatsAppButton';

const categoryIcons = {
  harmony: harmonyIcon,
  prosperity: prosperityIcon,
  love: loveIcon,
};

export default function Home() {
  const { t } = useTranslation();
  
  console.log('Background image path:', fondoCajas);
  
  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
       
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        py: 8
      }}
    >
      <Container 
        maxWidth="lg" 
        sx={{
          mt: -5,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
        
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: -1,
            opacity: 0.8
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box textAlign="center" mb={8}>
            <Typography
              component="h1"
              variant="h2"
              gutterBottom
              sx={{ 
                fontFamily: 'Analogue',
                fontSize: '59px',
                lineHeight: '73.75px',
                color: '#50602A',
                mb: 4,
                '@font-face': {
                  fontFamily: 'Analogue',
                  src: 'url("/fonts/Analogue.otf") format("opentype")',
                  fontWeight: 'normal',
                  fontStyle: 'normal'
                }
              }}
            >
              The Kosmo Studio
            </Typography>
            <Typography
              variant="h5"
              paragraph
              sx={{ 
                fontFamily: 'Montserrat',
                fontSize: '19px',
                lineHeight: '22.8px',
                color: '#AF1F2E',
                textTransform: 'uppercase',
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                '@font-face': {
                  fontFamily: 'Montserrat',
                  src: 'url("/fonts/MONTSERRAT-REGULAR.TTF") format("truetype")',
                  fontWeight: 'normal',
                  fontStyle: 'normal'
                }
              }}
            >
              {t('home.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ 
                fontFamily: 'Montserrat',
                fontSize: '15px',
                lineHeight: '24px',
                color: '#50602A',
                maxWidth: '800px',
                mx: 'auto',
                mb: 4,
                '@font-face': {
                  fontFamily: 'Montserrat',
                  src: 'url("/fonts/MONTSERRAT-REGULAR.TTF") format("truetype")',
                  fontWeight: 'normal',
                  fontStyle: 'normal'
                }
              }}
            >
              {t('home.description')}
            </Typography>
            <Button
              to="/products"
              variant="contained"
              color="primary"
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5,
                background: 'linear-gradient(45deg, #EC24227, #EA5A28)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #EC24227, #EA5A28)',
                }
              }}
            >
              {t('home.cta')}
            </Button>
          </Box>
        </motion.div>

        <Box
          sx={{
            width: '100%',
            height: '350px',
            position: 'relative',
            mt: 4,
            mb: 4,
            padding: 3
          }}
        >
          <img
            src={fondoCajas}
            alt="Background"
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'auto',
              height: '100%',
              objectFit: 'contain',
              zIndex: 0
            }}
          />
          <Grid 
            container 
            spacing={4}
            sx={{
              position: 'relative',
              zIndex: 1
            }}
          >
            {['harmony', 'prosperity', 'love'].map((category, index) => {
              const Icon = categoryIcons[category];
              return (
                <Grid item xs={12} md={4} key={category}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  >
                    <Card 
                      sx={{ 
                        height: '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-10px)',
                          boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.45)'
                        }
                      }}
                    >
                      <CardContent sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 4
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          <img 
                            src={Icon} 
                            alt={category} 
                            style={{ 
                              width: '80px', 
                              height: '80px',
                              objectFit: 'contain'
                            }} 
                          />
                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{ 
                              textAlign: 'center',
                              fontFamily: 'Montserrat',
                              fontSize: '19px',
                              lineHeight: '22.8px',
                              color: category === 'harmony' ? '#DF643C' : 
                                    category === 'prosperity' ? '#861E25' : 
                                    '#AF1F2E',
                              textTransform: 'uppercase',
                              '@font-face': {
                                fontFamily: 'Montserrat',
                                src: 'url("/fonts/MONTSERRAT-REGULAR.TTF") format("truetype")',
                                fontWeight: 'normal',
                                fontStyle: 'normal'
                              }
                            }}
                          >
                            {category}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            textAlign: 'center',
                            fontFamily: 'Montserrat',
                            fontSize: '15px',
                            lineHeight: '24px',
                            color: '#50602A',
                            mt: 2,
                            '@font-face': {
                              fontFamily: 'Montserrat',
                              src: 'url("/fonts/MONTSERRAT-REGULAR.TTF") format("truetype")',
                              fontWeight: 'normal',
                              fontStyle: 'normal'
                            }
                          }}
                        >
                          {t(`home.categories.${category}`)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </Box>
  );
}
